// IE Hub - Help & Support Page
// Displays external organisations that can provide help, advice, and support

import { useState } from 'react';
import { Box, Typography, Paper, Tabs, Tab, Grid, ArrowForwardIcon } from '../../../ui';
import DashboardLayout from '../../../components/DashboardLayout';
import HelpSupportService from '../../../shared/api/services/helpSupportService';
import { DashboardService } from '../../../shared/api/services/dashboardService';
import { ActionType } from '../../../shared/api/types';

// Support organization type
interface SupportOrganization {
  title: string;
  description: string;
  url: string;
}

// Support card component
interface SupportCardProps {
  title: string;
  description: string;
  url: string;
  onVisit?: (title: string, url: string) => void;
}

const SupportCard: React.FC<SupportCardProps> = ({ title, description, url, onVisit }) => {
  const handleVisit = () => {
    try {
      // Fire-and-forget logging; never block navigation
      if (onVisit) onVisit(title, url);
    } catch {}
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Paper data-testid="pages-private-from-dashboard-support-card-container-handle-visit"
      sx={{
        p: 3,
        borderRadius: '12px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2.25,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          transform: 'translateY(-2px)'
        }
      }}
      onClick={handleVisit}
    >
      <Box data-testid="pages-private-from-dashboard-support-card-container-action-1" sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography data-testid="pages-private-from-dashboard-support-card-text-action-1"
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '18px',
            color: '#343a40',
            lineHeight: 'normal'
          }}
        >
          {title}
        </Typography>
        <Typography data-testid="pages-private-from-dashboard-support-card-text-action-2"
          variant="body2"
          sx={{
            fontSize: '14px',
            color: '#6c757d',
            lineHeight: 'normal'
          }}
        >
          {description}
        </Typography>
      </Box>
      <Box data-testid="pages-private-from-dashboard-support-card-container-action-2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography data-testid="pages-private-from-dashboard-support-card-text-visit-site"
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            color: '#004b49'
          }}
        >
          Visit site
        </Typography>
        <ArrowForwardIcon sx={{ fontSize: '14px', color: '#004b49' }} />
      </Box>
    </Paper>
  );
};

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`support-tabpanel-${index}`}
      aria-labelledby={`support-tab-${index}`}
    >
      {value === index && <Box data-testid="pages-private-from-dashboard-tab-panel-container-action">{children}</Box>}
    </div>
  );
};

const HelpAndSupportPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    // Log tab change
    const tabLabels = ["Money and debt support", "Specialised support", "Additional information"];
    DashboardService.logButtonAction({
      actionType: ActionType.TAB_CLICK,
      actionName: `Support Tab: ${tabLabels[newValue] || newValue}`,
      pageName: "Support",
      subSection: "HelpAndSupport",
    }).catch(() => {});

    setActiveTab(newValue);
  };

  const handleExternalVisit = (linkName: string, linkAddress: string) => {
    // Log external link visit
    DashboardService.logButtonAction({
      actionType: ActionType.LINK_CLICK,
      actionName: `External Support: ${linkName}`,
      pageName: "Support",
      subSection: "HelpAndSupport",
    }).catch(() => {});

    const payload = {
      linkName,
      linkAddress,
    };
    // Fire-and-forget; errors are captured inside service and do not throw
    HelpSupportService.logExternalLink(payload, { skipErrorCapture: true });
  };

  // Support organisations data
  const moneyDebtSupport: SupportOrganization[] = [
    {
      title: 'Christians Against Poverty',
      description: 'Independent help to manage your debt issues.',
      url: 'https://capuk.org/'
    },
    {
      title: 'Ask Bill',
      description: 'Impartial help with money and bills.',
      url: 'https://www.askbill.org.uk/'
    },
    {
      title: 'Charis',
      description: 'View energy grants schemes open for applications.',
      url: 'https://charisgrants.com/individuals/'
    },
    {
      title: 'Citizens Advice',
      description: 'Free and impartial advice to get your benefits and resolve your issues with bills and debts.',
      url: 'https://www.citizensadvice.org.uk/'
    },
    {
      title: 'StepChange',
      description: 'Free and impartial debt advice over the phone and online.',
      url: 'https://moneyhealthcheck.stepchange.org/ie%20hub/outcomes/no-triage'
    },
    {
      title: 'Money Saving Expert',
      description: 'Independent information to cut your bills.',
      url: 'https://www.moneysavingexpert.com/'
    },
    {
      title: 'Lightning Reach',
      description: 'Find and apply for grants relevant to your circumstances and financial situation.',
      url: 'https://www.lightningreach.org/application-portal'
    },
    {
      title: 'Debt Camel',
      description: 'Independent information to help you out of debt.',
      url: 'https://debtcamel.co.uk/'
    },
    {
      title: 'British Gas Energy Trust',
      description: 'Impartial help with money and fuel bills.',
      url: 'https://britishgasenergytrust.org.uk/'
    },
    {
      title: 'Finding Finance',
      description: 'Search for providers of simple, fair, and affordable loans.',
      url: 'https://www.findingfinance.org.uk/'
    },
    {
      title: 'National Debt Line',
      description: 'Free and impartial debt advice over the phone and online.',
      url: 'https://www.nationaldebtline.org/'
    },
    {
      title: 'Money Helper',
      description: 'Government service that offers free and impartial money guidance.',
      url: 'https://www.moneyhelper.org.uk/en'
    },
    {
      title: 'Find a grant',
      description: 'Find and apply for grants relevant to your circumstances and financial situation.',
      url: 'https://www.find-government-grants.service.gov.uk/'
    },
    {
      title: 'Stop Loan Sharks',
      description: 'Stop Loan Sharks provides support and guidance about debts owing to an unlicensed lender or loan shark who lends money in an illegal way and might use intimidated tactics to make sure you pay them back.',
      url: 'https://www.stoploansharks.co.uk'
    }
  ];

  const specialisedSupport: SupportOrganization[] = [
    {
      title: 'Samaritans',
      description: 'Safe environment for you to talk and get emotional support.',
      url: 'https://www.samaritans.org/'
    },
    {
      title: 'Relate',
      description: 'Relationship counselling to strengthen any type of relationship.',
      url: 'https://www.relate.org.uk/'
    },
    {
      title: 'GamCare',
      description: 'Free advice and information for anyone harmed by gambling.',
      url: 'https://www.gamcare.org.uk/'
    },
    {
      title: 'TalkBanStop',
      description: 'A partnership between GamCare, Gamban and GAMSTOP - which brings together free tools and support for people to stop gambling online.',
      url: 'https://www.gamcare.org.uk/talk/'
    },
    {
      title: 'Frank',
      description: 'Honest information about drugs.',
      url: 'https://www.talktofrank.com/'
    },
    {
      title: 'Cruse',
      description: 'Support and information to help people go through a bereavement.',
      url: 'https://www.cruse.org.uk/'
    },
    {
      title: 'The Trussell Trust',
      description: 'Find a food bank near you.',
      url: 'https://www.trusselltrust.org/'
    },
    {
      title: 'Mental Health UK',
      description: 'Supports people whose lives are affected by mental health problems.',
      url: 'https://mentalhealth-uk.org/'
    },
    {
      title: 'Refuge',
      description: 'Free and confidential advice to understand your options and increase your safety.',
      url: 'https://www.nationaldahelpline.org.uk/'
    },
    {
      title: 'BeGambleAware',
      description: 'Free advice, tools, and information to help you make informed decisions about gambling.',
      url: 'https://www.begambleaware.org/'
    },
    {
      title: 'DrinkAware',
      description: 'Free advice and information to protect you from alcohol harm.',
      url: 'https://www.drinkaware.co.uk/'
    },
    {
      title: 'Action Fraud',
      description: 'National reporting centre if you have been scammed, defrauded or experienced cybercrime.',
      url: 'https://www.actionfraud.police.uk/'
    },
    {
      title: 'Shelter',
      description: 'Free advice and information for housing issues.',
      url: 'https://www.shelter.org.uk/'
    }
  ];

  const additionalInformation: SupportOrganization[] = [
    {
      title: 'Citizens Advice',
      description: 'Free and impartial advice to get your benefits and resolve your issues with bills and debts.',
      url: 'https://www.citizensadvice.org.uk/'
    },
    {
      title: 'Your Local council',
      description: 'Your local council offers free and impartial advice on benefits and debt issues.',
      url: 'https://www.gov.uk/find-local-council'
    },
    {
      title: 'Money Saving Expert',
      description: 'Independent information to cut your bills.',
      url: 'https://www.moneysavingexpert.com/'
    },
    {
      title: 'Vulnerability Registration Service',
      description: 'Register for free to help protect yourself.',
      url: 'https://www.vulnerabilityregistrationservice.co.uk/'
    },
    {
      title: 'Priority Services Register',
      description: 'Register for free priority support from your gas, electricity, and water providers.',
      url: 'https://www.thepsr.co.uk/'
    },
    {
      title: 'Energy Savings Trust',
      description: 'Impartial help to make your home more energy-efficient and lower your energy bills.',
      url: 'https://energysavingtrust.org.uk/'
    },
    {
      title: 'Save Water Save Money',
      description: 'Help to become more water efficient and lower your water bills. Request your free water saving devices.',
      url: 'https://www.savewatersavemoney.co.uk/'
    }
  ];

  return (
    <DashboardLayout backgroundColor="#f8f9fa">
      <Box data-testid="pages-private-from-dashboard-help-and-support-page-container-action-1" sx={{ py: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Header Section */}
        <Box data-testid="pages-private-from-dashboard-help-and-support-page-container-action-2">
          <Typography data-testid="pages-private-from-dashboard-help-and-support-page-text-help-&-support"
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '24px', md: '30px' },
              lineHeight: '36px',
              color: '#343a40',
              mb: 0.5
            }}
          >
            Our Partners
          </Typography>
          <Typography data-testid="pages-private-from-dashboard-help-and-support-page-text-action"
            variant="body1"
            sx={{
              fontSize: '16px',
              lineHeight: '24px',
              color: '#6c757d'
            }}
          >
            Find external organisations that can provide help, advice, and support.
          </Typography>
        </Box>

        {/* Tabs Section */}
        <Box data-testid="pages-private-from-dashboard-help-and-support-page-container-action-3" sx={{ borderBottom: 1, borderColor: '#dee2e6' }}>
          <Tabs data-testid="pages-private-from-dashboard-help-and-support-page-tabs-handle-tab-change"
            value={activeTab}
            onChange={handleTabChange}
            aria-label="support categories"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#004b49',
                height: '2px'
              },
              minHeight: '38px',
              '& .MuiTabs-scrollButtons.Mui-disabled': {
                opacity: 0.3,
              },
            }}
          >
            <Tab data-testid="pages-private-from-dashboard-help-and-support-page-tab-money-and-debt-support"
              label="Money and debt support"
              sx={{
                fontSize: '16px',
                fontWeight: activeTab === 0 ? 600 : 500,
                color: activeTab === 0 ? '#004b49' : '#adb5bd',
                textTransform: 'none',
                minHeight: '38px',
                '&.Mui-selected': {
                  color: '#004b49'
                }
              }}
            />
            <Tab data-testid="pages-private-from-dashboard-help-and-support-page-tab-specialised-support"
              label="Specialised support"
              sx={{
                fontSize: '16px',
                fontWeight: activeTab === 1 ? 600 : 500,
                color: activeTab === 1 ? '#004b49' : '#adb5bd',
                textTransform: 'none',
                minHeight: '38px',
                '&.Mui-selected': {
                  color: '#004b49'
                }
              }}
            />
            <Tab data-testid="pages-private-from-dashboard-help-and-support-page-tab-additional-information"
              label="Additional information"
              sx={{
                fontSize: '16px',
                fontWeight: activeTab === 2 ? 600 : 500,
                color: activeTab === 2 ? '#004b49' : '#adb5bd',
                textTransform: 'none',
                minHeight: '38px',
                '&.Mui-selected': {
                  color: '#004b49'
                }
              }}
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            {moneyDebtSupport.map((org, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <SupportCard
                  title={org.title}
                  description={org.description}
                  url={org.url}
                  onVisit={handleExternalVisit}
                />
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            {specialisedSupport.map((org, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <SupportCard
                  title={org.title}
                  description={org.description}
                  url={org.url}
                  onVisit={handleExternalVisit}
                />
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            {additionalInformation.map((org, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <SupportCard
                  title={org.title}
                  description={org.description}
                  url={org.url}
                  onVisit={handleExternalVisit}
                />
              </Grid>
            ))}
          </Grid>
        </TabPanel>
      </Box>
    </DashboardLayout>
  );
};

export default HelpAndSupportPage;

